/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router';

import { Box, Button, Grid } from '@mui/material';

import { Language } from 'shared';

import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { AuthenticationApiService } from '../../services/AuthenticationApiService';
import { RootState } from '../../store';
import { authorizeUserByToken } from '../../store/auth/actions';
import { ApiClientError } from '../../types/api/ApiClientError';
import { UnprocessableEntityPayload } from '../../types/api/UnprocessableEntityPayload';
import { unprocessibleEntityToObject } from '../../utils/unprocessibleEntityToObject';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useObjectState } from '../../utils/useObjectState';
import { LoginPageFormValues } from '../LoginPage/LoginPage.types';
import { SignUpUserForm } from './components/SignupUserForm/SignUpUserForm';
import { validationSchema } from './SignUpPage.consts';
import { createSignUpPageStyles } from './SignUpPage.styles';
import { ResetApiErrorForField, SignUpPageApiValidationErrors, SignUpPageFormValues } from './SignUpPage.types';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type State = {
  isFetching: boolean;
  errorMessage?: string;
  values: SignUpPageFormValues;
  apiErrors: SignUpPageApiValidationErrors;
};

const SignUpPageComponent = memo<PropsFromRedux>(props => {
  const { t } = useTranslation([Namespaces.Login, Namespaces.Common]);
  useDocumentTitle(t('page.login.sign_up'));
  const { auth } = props;
  const classes = createSignUpPageStyles();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const prefillEmail = searchParams.get('email');

  const [state, setState] = useObjectState<State>({
    isFetching: false,
    errorMessage: undefined,
    apiErrors: {},
    values: {
      email: prefillEmail || '',
      firstName: '',
      language: 'en',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
  });
  const { values, apiErrors, errorMessage, isFetching } = state;

  const submit = async (data: SignUpPageFormValues) => {
    const apiParameters = {
      email: data.email.trim(),
      password: data.password.trim(),
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      language: data.language.trim() as Language,
    };
    try {
      setState({ isFetching: true, apiErrors: {}, errorMessage: undefined });
      const token = await AuthenticationApiService.register(apiParameters);
      dispatch(authorizeUserByToken(token.data.payload.accessToken, new Date(token.data.payload.expires)));
      setState({ isFetching: false });
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      if (error.response?.data.message === 'unprocessable_entity') {
        const payload: UnprocessableEntityPayload<keyof LoginPageFormValues> = error.response.data.payload;
        setState({
          apiErrors: unprocessibleEntityToObject(payload),
          isFetching: false,
        });
      } else {
        setState({
          isFetching: false,
          errorMessage: error.response?.data.message,
        });
      }
    }
  };

  const resetApiError: ResetApiErrorForField = name => {
    if (apiErrors[name]) {
      setState({ apiErrors: { ...apiErrors, [name]: undefined } });
    }
  };

  return (
    <>
      <Box className={classes.formTitle} fontWeight={500}>
        {t(`${Namespaces.Login}:page.login.sign_up`)}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
        <Formik initialValues={values} validationSchema={validationSchema} onSubmit={submit}>
          {props => (
            <SignUpUserForm
              {...props}
              isFetching={isFetching || auth.isFetching}
              apiErrors={apiErrors}
              resetApiError={resetApiError}
              actions={
                <>
                  <Grid item xs>
                    <Link className={classes.forgotStyle} to={BasicRoutes.LOGIN}>
                      <Button size={'small'}>{t(`${Namespaces.Login}:page.forgot_password.back_to_login`)}</Button>
                    </Link>
                  </Grid>
                  <Grid item>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      size="small"
                      disabled={isFetching}
                      data-cy="signup-form-submit-button"
                    >
                      {t(`${Namespaces.Login}:page.login.sign_up`)}
                    </Button>
                  </Grid>
                </>
              }
            />
          )}
        </Formik>
        <Box fontWeight="500" className={classes.errorBlock} data-cy="signup-form-error">
          {errorMessage && errorMessage !== 'no_invitation' && (
            <p className={classes.errorLine}>
              {t([
                `${Namespaces.Login}:page.login.error.${errorMessage}`,
                `${Namespaces.Login}:page.login.error.uncaught_error`,
              ])}
            </p>
          )}
        </Box>
      </Box>
      <SimpleNotifyModal
        open={errorMessage === 'no_invitation'}
        message={t(`${Namespaces.Login}:page.login.error.${errorMessage}`)}
        onOk={() => setState({ errorMessage: undefined })}
      />
    </>
  );
});

export const SignUpPage = connector(SignUpPageComponent);
